import React, { useState, useContext, useMemo } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  LinearProgress,
  Collapse,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/authContext';
import { Tenant } from '../../models/tenant';
import { Vehicle } from '../../models/vehicle';
import { isPushNotificationsEnabled } from '../../utils/pushNotificationUtils';
import { SMTPSource, IMAPSource } from '../../models/email-service';

interface OnboardingChecklistProps {
  tenant: Tenant | null;
  vehicles: Vehicle[];
  onOpenSettings: (tab?: number) => void;
  onOpenVehicleImport: () => void;
  onNavigateToLoadSearch: () => void;
  compact?: boolean;
}

interface ChecklistStep {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  isComplete: boolean;
  action?: () => void | Promise<void>;
  actionLabel: string;
  required: boolean;
}

const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  tenant,
  vehicles,
  onOpenSettings,
  onOpenVehicleImport,
  onNavigateToLoadSearch,
  compact = false,
}) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [expanded, setExpanded] = useState(true);
  const [enablingPush, setEnablingPush] = useState(false);

  // Calculate completion status for each step
  const pushEnabled = useMemo(() => (
    // Check both the local state (immediate) and the ARN (persisted)
    authContext?.pushNotificationsEnabled || isPushNotificationsEnabled(authContext?.user?.platformEndpointArn)
  ), [authContext?.pushNotificationsEnabled, authContext?.user?.platformEndpointArn]);

  const emailConnected = useMemo(() => {
    // smtpSource/imapSource < 98 means configured (98=ERRORED, 99=NONE)
    const smtpConfigured = tenant?.smtpSource !== undefined && 
      tenant.smtpSource !== SMTPSource.NONE && 
      tenant.smtpSource !== SMTPSource.ERRORRED;
    const imapConfigured = tenant?.imapSource !== undefined && 
      tenant.imapSource !== IMAPSource.NONE && 
      tenant.imapSource !== IMAPSource.ERRORRED;
    return smtpConfigured && imapConfigured;
  }, [tenant?.smtpSource, tenant?.imapSource]);

  const platformsConnected = useMemo(() => Boolean(tenant?.enabledMarketplaces && tenant.enabledMarketplaces.length > 0), [tenant?.enabledMarketplaces]);

  const searchStrategyConfigured = useMemo(() => {
    const hasPreferredDestinations = tenant?.preferredDestinations && 
      Object.keys(tenant.preferredDestinations).length > 0;
    const hasWorkingCountries = tenant?.workingCountries && 
      tenant.workingCountries.length > 0;
    return Boolean(hasPreferredDestinations || hasWorkingCountries);
  }, [tenant?.preferredDestinations, tenant?.workingCountries]);

  const vehiclesImported = useMemo(() => vehicles.length > 0, [vehicles]);

  const handleEnablePush = async () => {
    if (!authContext?.enablePushNotifications) return;
    
    setEnablingPush(true);
    try {
      const success = await authContext.enablePushNotifications();
      if (!success) {
        console.error('Failed to enable push notifications');
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
    } finally {
      setEnablingPush(false);
    }
  };

  const steps: ChecklistStep[] = [
    {
      id: 'notifications',
      icon: <NotificationsActiveIcon />,
      title: t('Enable Push Notifications'),
      description: t('Get notified when AI needs your input or finds great matches'),
      isComplete: pushEnabled,
      action: handleEnablePush,
      actionLabel: enablingPush ? t('Enabling...') : t('Enable'),
      required: true,
    },
    {
      id: 'email',
      icon: <EmailIcon />,
      title: t('Connect Email'),
      description: t('Link your email to send and receive negotiation messages'),
      isComplete: emailConnected,
      action: () => onOpenSettings(7), // Email tab (index 7)
      actionLabel: t('Configure'),
      required: true,
    },
    {
      id: 'platforms',
      icon: <SettingsIcon />,
      title: t('Connect Load Platforms'),
      description: t('Connect TIMOCOM, Teleroute, or other freight exchanges'),
      isComplete: platformsConnected,
      action: () => onOpenSettings(10), // Integrations tab (index 10)
      actionLabel: t('Connect'),
      required: true,
    },
    {
      id: 'strategy',
      icon: <SettingsIcon />,
      title: t('Configure Search Strategy'),
      description: t('Set preferred destinations, routes, and scoring weights'),
      isComplete: searchStrategyConfigured,
      action: () => onOpenSettings(2), // Destinations tab (index 2)
      actionLabel: t('Configure'),
      required: true,
    },
    {
      id: 'vehicles',
      icon: <LocalShippingIcon />,
      title: t('Import Your Fleet'),
      description: t('Add your trucks to start finding loads'),
      isComplete: vehiclesImported,
      action: onOpenVehicleImport,
      actionLabel: t('Import'),
      required: true,
    },
    {
      id: 'automation',
      icon: <SmartToyIcon />,
      title: t('Enable AI Automation'),
      description: t('Turn on auto-search and auto-negotiation for your trucks'),
      isComplete: false, // This is the goal, always show as incomplete until they do it
      action: onNavigateToLoadSearch,
      actionLabel: t('Start'),
      required: false,
    },
  ];

  const totalRequiredSteps = steps.filter(s => s.required).length;
  const requiredCompleted = steps.filter(s => s.required && s.isComplete).length;
  const progress = Math.round((requiredCompleted / totalRequiredSteps) * 100);
  const isSetupComplete = requiredCompleted === totalRequiredSteps;

  // Find the first incomplete step
  const currentStepIndex = steps.findIndex(s => !s.isComplete);
  const activeStep = currentStepIndex === -1 ? steps.length : currentStepIndex;

  // Don't render until we have tenant data to avoid flash
  if (!tenant) {
    return null;
  }

  if (isSetupComplete && compact) {
    return null; // Hide in compact mode when complete
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 2 : 3,
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: isSetupComplete ? 'success.light' : 'primary.light',
        background: (theme) => isSetupComplete 
          ? `linear-gradient(135deg, ${theme.palette.success.light}10 0%, ${theme.palette.success.main}05 100%)`
          : `linear-gradient(135deg, ${theme.palette.primary.light}10 0%, ${theme.palette.primary.main}05 100%)`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SmartToyIcon sx={{ color: isSetupComplete ? 'success.main' : 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {isSetupComplete ? t('Setup Complete!') : t('Get Started with AI Automation')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isSetupComplete 
                ? t('Your account is ready for full automation')
                : t('Complete these steps to unlock automated load search and negotiation')
              }
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip 
            label={`${requiredCompleted}/${totalRequiredSteps} ${t('complete')}`}
            color={isSetupComplete ? 'success' : 'primary'}
            size="small"
          />
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Progress bar */}
      <LinearProgress 
        variant="determinate" 
        value={progress} 
        sx={{ 
          mb: expanded ? 3 : 0, 
          height: 8, 
          borderRadius: 4,
          bgcolor: 'background.paper',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: isSetupComplete ? 'success.main' : 'primary.main',
          }
        }} 
      />

      {/* Steps */}
      <Collapse in={expanded}>
        <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
          {steps.map((step, index) => (
            <Step key={step.id} completed={step.isComplete}>
              <StepLabel
                StepIconComponent={() => (
                  step.isComplete 
                    ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                    : <RadioButtonUncheckedIcon sx={{ color: index === activeStep ? 'primary.main' : 'text.disabled' }} />
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontWeight: index === activeStep ? 600 : 400,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.icon}
                  <Typography variant="subtitle2">{step.title}</Typography>
                  {step.required && !step.isComplete && (
                    <Chip label={t('Required')} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {step.description}
                </Typography>
                {!step.isComplete && step.action && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={step.action}
                    disabled={step.id === 'notifications' && enablingPush}
                    sx={{ mr: 1 }}
                  >
                    {step.actionLabel}
                  </Button>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {isSetupComplete && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t("You're all set! Go to Load Search to enable AI automation for your trucks.")}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={onNavigateToLoadSearch}
              sx={{ mt: 1 }}
            >
              {t('Go to Load Search')}
            </Button>
          </Alert>
        )}
      </Collapse>
    </Paper>
  );
};

export default OnboardingChecklist;
